import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Chip,
  Paper,
  Grid,
  Collapse,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Delete,
  Translate,
  CheckCircle,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import {
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';

const GlossaryTermDialog = ({ 
  open, 
  onClose, 
  onSaved, 
  glossaryId, 
  term, 
  languages
}) => {
  const [englishLanguage, setEnglishLanguage] = useState(null);
  
  // Core term data (English is the reference)
  const [formData, setFormData] = useState({
    glossary: glossaryId,
    english_text: '',
    term_type: 'word',
    is_active: true
  });
  
  // Source language translations (FROM other languages TO English)
  const [sourceTranslations, setSourceTranslations] = useState({});
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedTranslations, setExpandedTranslations] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    // Find English language
    const english = languages.find(lang => lang.code === 'en');
    setEnglishLanguage(english);
  }, [languages]);

  useEffect(() => {
    if (term) {
      setFormData({
        glossary: term.glossary,
        english_text: term.source_text,
        term_type: term.term_type,
        is_active: term.is_active
      });
      // Load existing translations
      loadTranslations();
    } else {
      resetForm();
    }
  }, [term, glossaryId]);

  const resetForm = () => {
    setFormData({
      glossary: glossaryId,
      english_text: '',
      term_type: 'word',
      is_active: true
    });
    setSourceTranslations({});
    setError('');
    setValidationErrors({});
  };

  const loadTranslations = async () => {
    if (!term?.id) return;
    
    try {
      const response = await axios.get(`terminology/glossary-terms/${term.id}/translations/`);
      const translationsData = response.data.results || response.data || [];
      
      // Convert translations array to object keyed by language ID
      const translationsObj = {};
      translationsData.forEach(trans => {
        const langId = trans.language?.id || trans.language;
        translationsObj[langId] = trans.translated_text;
      });
      setSourceTranslations(translationsObj);
    } catch (error) {
      console.error('Error loading translations:', error);
      // If endpoint doesn't exist, use translations from term object
      if (term.translations) {
        const translationsObj = {};
        term.translations.forEach(trans => {
          const langId = trans.language?.id || trans.language;
          translationsObj[langId] = trans.translated_text;
        });
        setSourceTranslations(translationsObj);
      }
    }
  };

  /**
   * Get languages available for source translations
   * Excludes English (since it's the reference language)
   */
  const getAvailableSourceLanguages = () => {
    return languages.filter(lang => lang.code !== 'en');
  };

  /**
   * Handle source translation text change
   */
  const handleTranslationChange = (langId, value) => {
    setSourceTranslations(prev => ({
      ...prev,
      [langId]: value
    }));
  };

  /**
   * Clear translation for a language
   */
  const clearTranslation = (langId) => {
    const newTranslations = { ...sourceTranslations };
    delete newTranslations[langId];
    setSourceTranslations(newTranslations);
  };

  /**
   * Get count of filled source translations
   */
  const getFilledTranslationsCount = () => {
    return Object.values(sourceTranslations).filter(t => t && t.trim()).length;
  };

  const handleSubmit = async () => {
    if (!formData.english_text) {
      setError('English text is required');
      return;
    }

    if (!englishLanguage) {
      setError('English language not found in system');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        glossary: formData.glossary,
        source_text: formData.english_text,
        term_type: formData.term_type,
        source_language_id: englishLanguage.id,
        is_active: formData.is_active
      };

      let termId;
      if (term) {
        await axios.put(`terminology/glossary-terms/${term.id}/`, payload);
        termId = term.id;
      } else {
        const response = await axios.post('terminology/glossary-terms/', payload);
        termId = response.data.id;
      }

      // Update translations
      // First, get existing translations to compare
      let existingTranslations = [];
      try {
        const response = await axios.get(`terminology/glossary-terms/${termId}/translations/`);
        existingTranslations = response.data.results || response.data || [];
      } catch (error) {
        console.error('Error loading existing translations:', error);
      }

      // Create map of existing translations by language
      const existingMap = {};
      existingTranslations.forEach(trans => {
        const langId = trans.language?.id || trans.language;
        existingMap[langId] = trans;
      });

      // Process all translations
      const promises = [];
      
      // Update or create translations
      Object.entries(sourceTranslations).forEach(([langId, text]) => {
        if (text && text.trim()) {
          const payload = {
            glossary_term: termId,
            language_id: parseInt(langId),
            translated_text: text.trim()
          };

          if (existingMap[langId]) {
            // Update existing
            promises.push(
              axios.put(`terminology/glossary-term-translations/${existingMap[langId].id}/`, payload)
            );
          } else {
            // Create new
            promises.push(
              axios.post('terminology/glossary-term-translations/', payload)
            );
          }
        }
      });

      // Delete translations that were removed
      existingTranslations.forEach(trans => {
        const langId = trans.language?.id || trans.language;
        if (!sourceTranslations[langId] || !sourceTranslations[langId].trim()) {
          promises.push(
            axios.delete(`terminology/glossary-term-translations/${trans.id}/`)
          );
        }
      });

      await Promise.all(promises);

      onSaved();
    } catch (error) {
      console.error('Error saving term:', error);
      setError(error.response?.data?.message || error.response?.data?.detail || 'Error saving term');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Translate color="primary" />
          <Typography variant="h6">
            {term ? 'Edit Glossary Term' : 'Add Glossary Term'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        
        {/* SECTION 1: English Term (Reference Language) */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3,
            mb: 3,
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3,
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1.125rem'
            }}
          >
            English Term (Reference Language)
          </Typography>
          
          <Stack spacing={3}>
            {/* Term Type */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}
              >
                Term Type
              </Typography>
              <ToggleButtonGroup
                value={formData.term_type}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setFormData({ ...formData, term_type: newValue });
                  }
                }}
                fullWidth
                color="primary"
              >
                <ToggleButton value="word" sx={{ py: 1.5 }}>
                  Word
                </ToggleButton>
                <ToggleButton value="acronym" sx={{ py: 1.5 }}>
                  Acronym
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* English Text */}
            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              maxRows={6}
              label="English Text"
              value={formData.english_text}
              onChange={(e) => setFormData({ ...formData, english_text: e.target.value })}
              error={!!validationErrors.english_text}
              helperText={validationErrors.english_text || "The term or phrase in English (reference language)"}
              autoFocus
            />

            {/* Active Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </Paper>

        {/* SECTION 2: Source Language Translations */}
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between"
            sx={{ cursor: 'pointer' }}
            onClick={() => setExpandedTranslations(!expandedTranslations)}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                Source Language Translations
              </Typography>
              <Chip 
                label={`${getFilledTranslationsCount()} of ${getAvailableSourceLanguages().length}`}
                size="small"
                color={getFilledTranslationsCount() > 0 ? "success" : "default"}
              />
            </Box>
            <IconButton size="small">
              {expandedTranslations ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={expandedTranslations}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Provide the same term in other languages (translations FROM source languages TO English)
              </Typography>

              {getAvailableSourceLanguages().length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No source languages available for translation.
                </Alert>
              ) : (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {getAvailableSourceLanguages().map((lang) => (
                    <Paper 
                      key={lang.id}
                      variant="outlined" 
                      sx={{ 
                        p: 2,
                        bgcolor: sourceTranslations[lang.id] ? 'success.50' : 'background.paper'
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {lang.name} ({lang.code.toUpperCase()})
                        </Typography>
                        {lang.name_native && lang.name_native !== lang.name && (
                          <Typography variant="caption" color="text.secondary">
                            - {lang.name_native}
                          </Typography>
                        )}
                        {sourceTranslations[lang.id] && (
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              clearTranslation(lang.id);
                            }}
                            sx={{ ml: 'auto' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={`Term in ${lang.name}...`}
                        value={sourceTranslations[lang.id] || ''}
                        onChange={(e) => handleTranslationChange(lang.id, e.target.value)}
                      />
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Collapse>
        </Paper>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading || !formData.english_text}
          startIcon={loading ? <CircularProgress size={20} /> : (term ? <CheckCircle /> : <Add />)}
        >
          {loading ? 'Saving...' : (term ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlossaryTermDialog;
